import { useState, useRef, useCallback, useEffect } from "react";
import MDEditor from "@uiw/react-markdown-editor";
import MarkdownPreview from "@uiw/react-markdown-preview";
import "@uiw/react-markdown-preview/markdown.css";
import "@uiw/react-markdown-editor/markdown-editor.css";
import { useTranslation } from "../i18n";
import { Label } from "./ui/label";
import { apiFetch } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * Markdown editor with toolbar, server-side preview, and placeholder help.
 *
 * The built-in editor preview is disabled; instead, a separate preview panel
 * to the right renders backend-produced HTML with placeholders resolved.
 *
 * Props:
 *  - value: string
 *  - onChange: (string) => void
 *  - label?: string
 *  - triggerType?: string  — trigger type key for server preview
 *  - placeholders?: Array<{key: string, description: string, example: string|number}>
 *                    — backend-driven list of available placeholders
 */
export default function MarkdownEditor({
  value,
  onChange,
  label,
  triggerType,
  placeholders = [],
}) {
  const { t } = useTranslation();
  const [showHelp, setShowHelp] = useState(false);
  const [preview, setPreview] = useState({ title: "", body: "" });
  const debounceRef = useRef(null);

  const fetchPreview = useCallback((text, type) => {
    if (!type) {
      setPreview({ title: "", body: text || "" });
      return;
    }
    apiFetch(`${HOST}/api/v1/campaigns/render-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        triggerType: type,
        bodyTemplate: text || "",
      }),
    })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("preview failed");
      })
      .then((data) =>
        setPreview({ title: data.title || "", body: data.body || "" }),
      )
      .catch(() => {
        setPreview({ title: "", body: text || "" });
      });
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  /** Debounced preview — fires 500 ms after the last keystroke. */
  const debouncedPreview = useCallback(
    (text, type) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchPreview(text, type), 500);
    },
    [fetchPreview],
  );

  const handleChange = useCallback(
    (text) => {
      onChange(text);
      debouncedPreview(text, triggerType);
    },
    [onChange, debouncedPreview, triggerType],
  );

  /** Final preview fetch on blur — no debounce, immediate. */
  const handleBlur = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchPreview(value, triggerType);
  }, [fetchPreview, value, triggerType]);

  // Fetch initial preview when trigger type changes
  useEffect(() => {
    fetchPreview(value, triggerType);
  }, [triggerType, fetchPreview]);

  return (
    <div>
      {label && (
        <div className="flex items-center gap-2 mb-1">
          <Label>{label}</Label>
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="text-c-gray-400 hover:text-white text-xs underline"
          >
            {t("campaign.placeholders")}
          </button>
        </div>
      )}

      {showHelp && (
        <div className="bg-c-gray-500/60 border border-c-gray-400/20 rounded-lg p-3 mb-2 text-xs text-c-gray-300">
          <p className="mb-2 font-medium text-white">
            {t("campaign.placeholdersAvailable")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {placeholders.map(({ key, example, description }) => (
              <div key={key} className="flex items-baseline gap-2">
                <code className="text-accent whitespace-nowrap">{key}</code>
                <span className="text-c-gray-400" title={description}>
                  → {example}
                </span>
                <span className="text-c-gray-400 italic hidden lg:inline">
                  — {description}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 items-stretch">
        {/* Editor pane — built-in preview disabled */}
        <div
          data-color-mode="dark"
          className="rounded-lg overflow-hidden flex-1 min-w-0 md-editor-wrap"
        >
          <MDEditor
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            height={300}
            preview="none"
            toolbars={[
              "bold",
              "italic",
              "strike",
              "heading",
              "link",
              "olist",
              "ulist",
              "quote",
              "code",
              "codeBlock",
              "undo",
              "redo",
            ]}
          />
        </div>

        {/* Preview pane — renders backend-produced HTML */}
        <div className="hidden md:flex flex-col w-[375px] shrink-0">
          <div className="text-xs text-c-gray-400 mb-1 font-medium">
            {t("campaign.previewNotifications")}
          </div>
          <div
            data-color-mode="dark"
            className="flex-1 rounded-lg border border-c-gray-400/20 overflow-auto campaign-preview"
            style={{
              backgroundColor: "#1B283F",
            }}
          >
            {preview.title && (
              <div className="px-4 pt-4 pb-1 text-sm font-semibold text-white border-b border-c-gray-400/10">
                {preview.title}
              </div>
            )}
            <div className="px-4 py-3">
              <MarkdownPreview
                source={preview.body}
                style={{
                  backgroundColor: "transparent",
                  color: "#f4f4f4",
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Hide the built-in editor preview pane */
        .md-editor-preview { display: none !important; }

        /* Hide line numbers gutter */
        .md-editor .cm-gutters { display: none !important; }
        .md-editor .cm-content { margin-left: 0 !important; }
        .md-editor .cm-lineNumbers { display: none !important; }

        /* Remove the CodeMirror left padding that creates space for line numbers */
        .md-editor .cm-scroller { padding-left: 0 !important; }

        /* Enable word wrap instead of horizontal scrolling */
        .md-editor .cm-content { white-space: pre-wrap !important; word-wrap: break-word !important; }
        .md-editor .cm-line { white-space: pre-wrap !important; }

        .cm-activeLine { background-color: transparent !important; }

        /* Mobile-like preview container */
        .campaign-preview .wmde-markdown {
          background-color: transparent;
          color: #f4f4f4;
          font-size: 14px;
          line-height: 1.6;
          padding: 0;
        }

        /* Dark mode markdown token colors */
        .campaign-preview .wmde-markdown,
        .campaign-preview[data-color-mode*='dark'] .wmde-markdown {
          --color-fg-default: #f4f4f4;
          --color-canvas-default: #1B283F;
          --color-canvas-subtle: #1B283F;
          --color-fg-muted: #727A88;
          --color-fg-subtle: #727A88;
          --color-border-muted: #2a3955;
        }
      `}</style>
    </div>
  );
}
